/**
 * Formatting utilities for users.
 * Uses Intl APIs so output is locale-aware without hardcoded strings.
 */
import { __ } from '@/hub-shared/utils/i18n';

function resolveFormatLocale(locale?: string): string {
  const fromParam = locale?.trim();
  if (fromParam !== undefined && fromParam !== '') {
    return fromParam.replace('_', '-');
  }
  const raw =
    typeof document !== 'undefined'
      ? (document.documentElement?.lang ?? '').trim()
      : '';
  return raw !== '' ? raw.replace('_', '-') : 'fr-FR';
}

/**
 * Format a date string as a locale-aware relative time (e.g. "il y a 2 jours").
 * Accepts an optional BCP 47 locale string; defaults to document.documentElement.lang.
 */
export const formatRelativeDate = (dateString: string, locale?: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const resolvedLocale = resolveFormatLocale(locale);
  const rtf = new Intl.RelativeTimeFormat(resolvedLocale, { numeric: 'auto' });

  if (Math.abs(diffInSeconds) < 60) {
    return rtf.format(0, 'second');
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (Math.abs(diffInMinutes) < 60) {
    return rtf.format(-diffInMinutes, 'minute');
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (Math.abs(diffInHours) < 24) {
    return rtf.format(-diffInHours, 'hour');
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (Math.abs(diffInDays) < 7) {
    return rtf.format(-diffInDays, 'day');
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (Math.abs(diffInWeeks) < 4) {
    return rtf.format(-diffInWeeks, 'week');
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (Math.abs(diffInMonths) < 12) {
    return rtf.format(-diffInMonths, 'month');
  }

  const diffInYears = Math.floor(diffInDays / 365);
  return rtf.format(-diffInYears, 'year');
};

/**
 * Get a translatable display name for a WordPress role slug.
 * New roles should be added here and to the .pot file.
 */
export const getRoleDisplayName = (role: string): string => {
  const roleNames: Record<string, string> = {
    customer: __('Admin Comptable'),
    expert_comptable: __('Expert Comptable'),
    collaborateur_comptable: __('Collaborateur Comptable'),
    utilisateur_entreprise: __('Utilisateur Entreprise'),
  };
  return roleNames[role] ?? role;
};

/**
 * Get a shared badge variant for a WordPress role slug.
 * Keeps table badge colors aligned across list and detail screens.
 */
export const getRoleBadgeVariant = (role: string): string => {
  const roleVariants: Record<string, string> = {
    customer: 'customer',
    expert_comptable: 'expert_comptable',
    collaborateur_comptable: 'collaborateur_comptable',
    utilisateur_entreprise: 'utilisateur_entreprise',
  };

  return roleVariants[role] ?? 'default';
};

